import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Alert, Box, LinearProgress,
} from '@mui/material';
import { Add, Delete, PlayArrow, Stop, CloudUpload, FileUpload } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

interface Tone {
  id: number;
  tenant_id: number;
  name: string;
  tone_key: string;
  call_type_code: string | null;
  unit_type: string | null;
  original_filename: string;
  file_size: number;
  content_type: string;
  checksum: string;
  notes: string | null;
}

export default function TonesPage() {
  const { tenantId } = useAuth();
  const [tones, setTones] = useState<Tone[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [name, setName] = useState('');
  const [toneKey, setToneKey] = useState('');
  const [callTypeCode, setCallTypeCode] = useState('');
  const [unitType, setUnitType] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [bulkFiles, setBulkFiles] = useState<File[]>([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [playingId, setPlayingId] = useState<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const res = await api.get(`/api/tenants/${tenantId}/tones`);
      setTones(res.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load tones');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async () => {
    if (!tenantId || !file) return;
    const formData = new FormData();
    formData.append('file', file);

    try {
      await api.post(`/api/tenants/${tenantId}/tones?name=${encodeURIComponent(name)}&tone_key=${encodeURIComponent(toneKey)}${callTypeCode ? `&call_type_code=${encodeURIComponent(callTypeCode)}` : ''}${unitType ? `&unit_type=${encodeURIComponent(unitType)}` : ''}${notes ? `&notes=${encodeURIComponent(notes)}` : ''}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDialogOpen(false);
      setFile(null);
      setName('');
      setToneKey('');
      setCallTypeCode('');
      setUnitType('');
      setNotes('');
      load();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Upload failed');
    }
  };

  const handleBulkUpload = async () => {
    if (!tenantId || bulkFiles.length === 0) return;
    setBulkUploading(true);
    setError(null);
    setSuccess(null);

    const formData = new FormData();
    bulkFiles.forEach(f => formData.append('files', f));

    try {
      const res = await api.post(`/api/tenants/${tenantId}/tones/bulk`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { uploaded, errors } = res.data;
      if (errors && errors.length > 0) {
        setError(`Some files failed: ${errors.join('; ')}`);
      }
      if (uploaded && uploaded.length > 0) {
        setSuccess(`Successfully uploaded ${uploaded.length} tone${uploaded.length > 1 ? 's' : ''}`);
        setTimeout(() => setSuccess(null), 5000);
      }
      setBulkDialogOpen(false);
      setBulkFiles([]);
      load();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Bulk upload failed');
    } finally {
      setBulkUploading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!tenantId || !window.confirm('Delete this tone?')) return;
    try {
      await api.delete(`/api/tenants/${tenantId}/tones/${id}`);
      load();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Delete failed');
    }
  };

  const handlePlay = (t: Tone) => {
    if (playingId === t.id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    if (audioRef.current) audioRef.current.pause();
    const audio = new Audio(`/api/tenants/${tenantId}/tones/${t.id}/download`);
    audio.onended = () => setPlayingId(null);
    audio.onerror = () => {
      setError('Audio playback failed');
      setPlayingId(null);
    };
    audio.play().catch(() => setError('Browser blocked audio playback'));
    audioRef.current = audio;
    setPlayingId(t.id);
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isAudioFile = (f: File) =>
    f.type === 'audio/wav' || f.type === 'audio/x-wav' || f.type === 'audio/mpeg' ||
    f.type === 'audio/mp3' || f.name.endsWith('.wav') || f.name.endsWith('.mp3');

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFiles = Array.from(e.dataTransfer.files).filter(isAudioFile);
    if (droppedFiles.length > 0) {
      setBulkFiles(prev => [...prev, ...droppedFiles]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selected = Array.from(e.target.files).filter(isAudioFile);
      setBulkFiles(prev => [...prev, ...selected]);
    }
    e.target.value = '';
  };

  const removeBulkFile = (index: number) => {
    setBulkFiles(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4">Alert Tones</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<FileUpload />} onClick={() => { setBulkFiles([]); setBulkDialogOpen(true); }}>
            Bulk Upload
          </Button>
          <Button variant="contained" startIcon={<CloudUpload />} onClick={() => setDialogOpen(true)}>Upload Tone</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Tone Key</TableCell>
              <TableCell>Call Type</TableCell>
              <TableCell>Unit Type</TableCell>
              <TableCell>File</TableCell>
              <TableCell>Size</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} align="center">Loading...</TableCell></TableRow>
            ) : tones.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center">No tones uploaded</TableCell></TableRow>
            ) : tones.map(t => (
              <TableRow key={t.id}>
                <TableCell>{t.name}</TableCell>
                <TableCell><code>{t.tone_key}</code></TableCell>
                <TableCell>{t.call_type_code || '—'}</TableCell>
                <TableCell>{t.unit_type || '—'}</TableCell>
                <TableCell>{t.original_filename}</TableCell>
                <TableCell>{formatSize(t.file_size)}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handlePlay(t)} color={playingId === t.id ? 'error' : 'primary'}>
                    {playingId === t.id ? <Stop fontSize="small" /> : <PlayArrow fontSize="small" />}
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(t.id)}><Delete fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Single upload dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Alert Tone</DialogTitle>
        <DialogContent>
          <TextField label="Tone Name" fullWidth margin="dense" value={name} onChange={e => setName(e.target.value)} required />
          <TextField label="Tone Key" fullWidth margin="dense" value={toneKey} onChange={e => setToneKey(e.target.value)} required
            helperText="Identifier used in routing rules (e.g., fire_tone, ems_tone)" />
          <TextField label="Call Type Code" fullWidth margin="dense" value={callTypeCode} onChange={e => setCallTypeCode(e.target.value)}
            helperText="Optional: auto-assign to this call type" />
          <TextField label="Unit Type" fullWidth margin="dense" value={unitType} onChange={e => setUnitType(e.target.value)}
            helperText="Optional: auto-assign to this unit type" />
          <TextField label="Notes" fullWidth margin="dense" value={notes} onChange={e => setNotes(e.target.value)} multiline rows={2} />
          <Box sx={{ mt: 2 }}>
            <Button variant="outlined" component="label" startIcon={<CloudUpload />}>
              {file ? file.name : 'Select WAV/MP3 File'}
              <input type="file" hidden accept=".wav,.mp3,audio/wav,audio/mpeg" onChange={e => setFile(e.target.files?.[0] || null)} />
            </Button>
            {file && <Typography variant="caption" sx={{ ml: 1 }}>{formatSize(file.size)}</Typography>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpload} disabled={!name || !toneKey || !file}>Upload</Button>
        </DialogActions>
      </Dialog>

      {/* Bulk upload dialog */}
      <Dialog open={bulkDialogOpen} onClose={() => !bulkUploading && setBulkDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Upload Alert Tones</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Drag and drop WAV/MP3 files below, or click to browse. Tone name and key are auto-generated from the filename.
          </Typography>

          <Box
            onDrop={handleDrop}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            sx={{
              border: '2px dashed',
              borderColor: dragOver ? 'primary.main' : 'divider',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              bgcolor: dragOver ? 'action.hover' : 'transparent',
              cursor: 'pointer',
              transition: 'all 0.2s',
              mb: 2,
            }}
            onClick={() => document.getElementById('bulk-file-input')?.click()}
          >
            <FileUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography color="text.secondary">
              {dragOver ? 'Drop files here' : 'Drag & drop WAV/MP3 files here, or click to browse'}
            </Typography>
            <input
              id="bulk-file-input"
              type="file"
              hidden
              multiple
              accept=".wav,.mp3,audio/wav,audio/mpeg"
              onChange={handleFileSelect}
            />
          </Box>

          {bulkFiles.length > 0 && (
            <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto' }}>
              <Table size="small">
                <TableBody>
                  {bulkFiles.map((f, i) => (
                    <TableRow key={i}>
                      <TableCell>{f.name}</TableCell>
                      <TableCell sx={{ width: 80 }}>{formatSize(f.size)}</TableCell>
                      <TableCell sx={{ width: 40 }} align="right">
                        <IconButton size="small" onClick={() => removeBulkFile(i)} disabled={bulkUploading}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}

          {bulkFiles.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {bulkFiles.length} file{bulkFiles.length > 1 ? 's' : ''} selected
              ({formatSize(bulkFiles.reduce((sum, f) => sum + f.size, 0))} total)
            </Typography>
          )}

          {bulkUploading && <LinearProgress sx={{ mt: 2 }} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDialogOpen(false)} disabled={bulkUploading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleBulkUpload}
            disabled={bulkFiles.length === 0 || bulkUploading}
            startIcon={<CloudUpload />}
          >
            {bulkUploading ? 'Uploading...' : `Upload ${bulkFiles.length} File${bulkFiles.length !== 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
